import {
  getBusinessThreadById,
  getBusinessThreads,
  getMyCognitionThreads,
  getPersonalThreads,
  getThreadPortals,
  getThreadVisitors,
  handleThreadMute,
  inviteUsersToThread,
  sharedSpaceCreateThread,
  sharedSpaceRemoveUser,
  updateThread,
} from '@/core/apis/share-space-api';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';

import { Thread, UserInfo } from '../types';

export class ThreadService {
  private static instance: ThreadService;
  private threadCache: Map<string, Thread> = new Map();

  private constructor() {}

  static getInstance(): ThreadService {
    if (!ThreadService.instance) {
      ThreadService.instance = new ThreadService();
    }
    return ThreadService.instance;
  }

  // Create a new thread
  async createThread(params: {
    userIds?: string[];
    threadName?: string;
    message?: string;
    vendorId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<Thread> {
    try {
      const response = await sharedSpaceCreateThread(params);

      if (response?.data) {
        const thread = response.data;
        this.threadCache.set(thread._id, thread);
        return thread;
      }

      throw new Error('Failed to create thread');
    } catch (error) {
      console.error('Failed to create thread:', error);
      throw error;
    }
  }

  // Get thread details
  async getThread(threadId: string): Promise<Thread> {
    try {
      // Check cache first
      const cached = this.threadCache.get(threadId);
      if (cached) {
        return cached;
      }

      const response = await getBusinessThreadById(threadId);

      if (response?.data) {
        const thread = response.data;
        this.threadCache.set(threadId, thread);
        return thread;
      } else if (response) {
        // Sometimes the response might be the thread directly
        this.threadCache.set(threadId, response);
        return response;
      }

      throw new Error('Thread not found');
    } catch (error) {
      console.error('Failed to get thread:', error);
      throw error;
    }
  }

  // Get thread list
  async getThreadList(params: {
    limit: number;
    offset: number;
    type: 'PERSONAL' | 'BUSINESS' | 'COGNITION';
    query?: string;
    users?: string[];
    vendorId?: string;
  }): Promise<{ threads: Thread[]; total: number }> {
    try {
      let response;

      // Use the appropriate API based on thread type
      switch (params.type) {
        case 'PERSONAL':
          response = await getPersonalThreads({
            limit: params.limit,
            offset: params.offset,
            type: 'PERSONAL',
            query: params.query || '',
          });
          break;
        case 'BUSINESS':
          response = await getBusinessThreads({
            limit: params.limit,
            offset: params.offset,
            vendorId: params.vendorId,
          });
          break;
        case 'COGNITION':
          response = await getMyCognitionThreads({
            limit: params.limit,
            offset: params.offset,
          });
          break;
        default:
          throw new Error(`Unknown thread type: ${params.type}`);
      }

      if (response) {
        // Cache threads
        const threads = response.threads || response.data?.threads || [];
        threads.forEach((thread: Thread) => {
          this.threadCache.set(thread._id, thread);
        });

        return {
          threads: threads,
          total: response.total || threads.length || 0,
        };
      }

      throw new Error('Failed to fetch threads');
    } catch (error) {
      console.error('Failed to fetch thread list:', error);
      throw error;
    }
  }

  // Mute/unmute thread
  async toggleMute(threadId: string, mute: boolean): Promise<void> {
    try {
      await handleThreadMute(threadId, { mute });

      // Update cache
      const cached = this.threadCache.get(threadId);
      if (cached) {
        cached.isMuted = mute;
        this.threadCache.set(threadId, cached);
      }
    } catch (error) {
      console.error('Failed to toggle mute:', error);
      throw error;
    }
  }

  // Add members to thread
  async addMembers(threadId: string, userIds: string[]): Promise<void> {
    try {
      await inviteUsersToThread(threadId, { users: userIds });

      // Clear cache to force refresh
      this.threadCache.delete(threadId);
    } catch (error) {
      console.error('Failed to add members:', error);
      throw error;
    }
  }

  // Remove member from thread
  async removeMember(threadId: string, userId: string): Promise<void> {
    try {
      await sharedSpaceRemoveUser(threadId, userId);

      // Update cache
      const cached = this.threadCache.get(threadId);
      if (cached && cached.participants) {
        cached.participants = cached.participants.filter((p) => p.Id !== userId);
        this.threadCache.set(threadId, cached);
      }
    } catch (error) {
      console.error('Failed to remove member:', error);
      throw error;
    }
  }

  // Leave thread (remove self)
  async leaveThread(threadId: string, userId: string): Promise<void> {
    return this.removeMember(threadId, userId);
  }

  // Update thread info
  async updateThreadInfo(
    threadId: string,
    updates: {
      threadName?: string;
      [key: string]: any;
    },
  ): Promise<Thread> {
    try {
      const response = await updateThread(threadId, updates);

      if (response?.data) {
        const updated = response.data;
        this.threadCache.set(threadId, updated);
        return updated;
      }

      throw new Error('Failed to update thread');
    } catch (error) {
      console.error('Failed to update thread:', error);
      throw error;
    }
  }

  // Get thread visitors/readers
  async getThreadVisitors(
    threadId: string,
    params: { limit: number; offset: number },
  ): Promise<{ visitors: UserInfo[]; total: number }> {
    try {
      const response = await getThreadVisitors(threadId, params);

      if (response?.data) {
        return {
          visitors: response.data.visitors || [],
          total: response.data.total || 0,
        };
      }

      return { visitors: [], total: 0 };
    } catch (error) {
      console.error('Failed to get thread visitors:', error);
      throw error;
    }
  }

  // Get thread portals
  async getThreadPortals(threadId: string): Promise<any[]> {
    try {
      const response = await getThreadPortals(threadId);
      return response?.data || [];
    } catch (error) {
      console.error('Failed to get thread portals:', error);
      throw error;
    }
  }

  // Clear thread from cache
  clearThreadCache(threadId: string): void {
    this.threadCache.delete(threadId);
  }

  // Clear all cache
  clearAllCache(): void {
    this.threadCache.clear();
  }

  // Get cached thread
  getCachedThread(threadId: string): Thread | undefined {
    return this.threadCache.get(threadId);
  }

  // Helper methods for thread state
  isUserInThread(thread: Thread, userId: string): boolean {
    return thread.participants?.some((p) => p.Id === userId) || false;
  }

  isBusinessThread(thread: Thread): boolean {
    return thread.threadType === 'BUSINESS' || !!thread.vendor;
  }

  getThreadDisplayName(thread: Thread): string {
    if (thread.threadName) {
      return thread.threadName;
    }

    if (thread.vendor?.businessName) {
      return thread.vendor.businessName;
    }

    if (thread.participants && thread.participants.length > 0) {
      const names = thread.participants
        .slice(0, 3)
        .map((p) => p.Name || p.businessName || 'Unknown')
        .join(', ');

      if (thread.participants.length > 3) {
        return `${names} +${thread.participants.length - 3}`;
      }

      return names;
    }

    return 'Unnamed Thread';
  }

  getThreadAvatar(thread: Thread): string | null {
    if (thread.vendor?.businessLogo) {
      return appendImageRepoDomainIfNotExistInUrl(thread.vendor.businessLogo);
    }

    if (thread.vendor?.businessProfileImage) {
      return appendImageRepoDomainIfNotExistInUrl(thread.vendor.businessProfileImage);
    }

    if (thread.participants && thread.participants.length === 1) {
      const participant = thread.participants[0];
      const avatar = participant.ProfileImage || (participant as any).DisplayPic || null;
      return avatar ? appendImageRepoDomainIfNotExistInUrl(avatar) : null;
    }

    return null;
  }

  // Format thread for display
  formatThreadForDisplay(thread: Thread): {
    id: string;
    displayName: string;
    avatar: string | null;
    lastMessage?: string;
    unreadCount: number;
    isMuted: boolean;
    isOnline: boolean;
    participantCount: number;
  } {
    return {
      id: thread._id,
      displayName: this.getThreadDisplayName(thread),
      avatar: this.getThreadAvatar(thread),
      lastMessage: thread.lastMessage?.message,
      unreadCount: thread.unreadCount || 0,
      isMuted: thread.isMuted || false,
      isOnline: true, // TODO: Implement online status
      participantCount: thread.participants?.length || 0,
    };
  }
}
